/* =========================================================================
   Grivet Shoe Quiz — main controller
   Phase 1 (state machine) + Phase 2 (scoring, results, cart, tier, email gate)
   Klaviyo wiring lands in Phase 3.
   ========================================================================= */
(function () {
  'use strict';

  const STORAGE_KEY = 'grivet_quiz_state_v1';
  const PROFILE_KEY = 'grivet_quiz_profile_v1';
  const PROFILE_TTL_DAYS = 90;
  const QUIZ_VERSION = '1.0';
  const TOTAL_STEPS = 7;

  const DEEP_LINK_MAP = { road: 'run_road', trail: 'run_trail', hike: 'hike', lifestyle: 'lifestyle', gift: 'gift' };

  const LOCAL_STATES = ['TN','MS','AR'];
  const LOCAL_METRO_STATES = { TN: ['memphis','germantown','collierville','bartlett','cordova','olive branch'], MS: ['olive branch','southaven','horn lake','hernando','oxford'], AR: ['west memphis','marion','jonesboro'] };
  const REGIONAL_STATES = ['TN','MS','AR','AL','KY','MO','LA','GA'];

  const root = document.querySelector('[data-quiz-root]');
  if (!root) return;

  /* --------------------------- State --------------------------- */
  const initialState = {
    started: false,
    stack: ['landing'],
    answers: {
      q1: null, q2: null, q3: null,
      q4: { loved: '', disliked: '', newbie: false },
      q5: [],
      q6: null,
      q7: { cushion: 3, feel: 3, drop: null, unsure: false },
      q8: { gender: '', size: '', width: '' }
    },
    completedAt: null,
    emailCaptured: null,
    smsOptIn: false,
    tier: null,
    detected: { city: null, region: null, country: null },
    top3: null,
    version: QUIZ_VERSION
  };

  let state = loadState();
  let catalog = null;

  /* -------------------------- Persistence --------------------------- */
  function loadState() {
    try {
      const saved = sessionStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed.version === QUIZ_VERSION) return parsed;
      }
    } catch {}
    return JSON.parse(JSON.stringify(initialState));
  }
  function saveState() { try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state)); } catch {} }
  function loadProfile() {
    try {
      const saved = localStorage.getItem(PROFILE_KEY);
      if (!saved) return null;
      const parsed = JSON.parse(saved);
      if (!parsed.savedAt || Date.now() - parsed.savedAt > PROFILE_TTL_DAYS * 86400000) {
        localStorage.removeItem(PROFILE_KEY);
        return null;
      }
      return parsed;
    } catch { return null; }
  }
  function saveProfile() {
    try {
      localStorage.setItem(PROFILE_KEY, JSON.stringify({
        answers: state.answers, completedAt: state.completedAt,
        email: state.emailCaptured, tier: state.tier,
        savedAt: Date.now(), version: QUIZ_VERSION
      }));
    } catch {}
  }

  /* -------------------------- Flow / branching --------------------------- */
  function nextScreen(current) {
    const a = state.answers;
    switch (current) {
      case 'landing': return 'q1';
      case 'q1':
        return { run_road: 'q2a', run_trail: 'q2b', hike: 'q2c', lifestyle: 'q2d', gift: 'q2e' }[a.q1] || 'q1';
      case 'q2a': case 'q2b': return 'q3';
      case 'q2c': case 'q2d': case 'q2e': return 'q4';
      case 'q3': return 'q4';
      case 'q4': return 'q5';
      case 'q5': return 'q6';
      case 'q6': return 'q7';
      case 'q7': return 'q8';
      case 'q8': return 'labor';
      case 'labor': return 'preview';
      case 'preview': return 'email-gate';
      case 'email-gate': return 'results';
      default: return null;
    }
  }
  function progressStep(id) {
    return { q1: 1, q2a: 2, q2b: 2, q2c: 2, q2d: 2, q2e: 2, q3: 3, q4: 4, q5: 5, q6: 6, q7: 7, q8: 7 }[id] || 0;
  }

  /* -------------------------- Rendering --------------------------- */
  const screens = root.querySelectorAll('[data-screen]');
  const progressEl = root.querySelector('[data-quiz-progress]');
  const progressFill = root.querySelector('[data-quiz-progress-fill]');
  const progressCur = root.querySelector('[data-quiz-progress-current]');
  const progressTot = root.querySelector('[data-quiz-progress-total]');
  if (progressTot) progressTot.textContent = TOTAL_STEPS;

  function announce(msg) {
    const el = root.querySelector('[data-sr-announce]');
    if (el) { el.textContent = ''; setTimeout(() => { el.textContent = msg; }, 50); }
  }

  function focusFirstInteractive(current) {
    const scope = root.querySelector('[data-screen="' + current + '"]');
    if (!scope) return;
    // Prefer an unchecked radio/checkbox tile, then next button, then first input
    const target =
      scope.querySelector('input[type="radio"]:not(:disabled), input[type="checkbox"]:not(:disabled)') ||
      scope.querySelector('input:not([type="hidden"]):not(:disabled), select:not(:disabled), textarea:not(:disabled)') ||
      scope.querySelector('[data-next], [data-submit], [data-start]');
    if (target && typeof target.focus === 'function') {
      setTimeout(() => target.focus({ preventScroll: true }), 50);
    }
  }

  function renderCurrent() {
    const current = state.stack[state.stack.length - 1];
    screens.forEach(el => { el.hidden = el.dataset.screen !== current; });
    const step = progressStep(current);
    if (step > 0) {
      progressEl.hidden = false;
      progressFill.style.setProperty('--gq-progress', (step / TOTAL_STEPS * 100) + '%');
      progressCur.textContent = step;
      announce('Step ' + step + ' of ' + TOTAL_STEPS);
    } else { progressEl.hidden = true; }
    restoreAnswers(current);
    updateNavButtons(current);
    if (current === 'labor') { runLaborIllusion(); announce('Building your fit profile'); }
    if (current === 'preview') { renderPreview(); announce('Your results are ready. Enter your email to unlock.'); }
    if (current === 'results') {
      renderResults();
      announce('Showing your 3 shoe matches');
      try { window.GrivetQuizKlaviyo && window.GrivetQuizKlaviyo.viewedResults(state.top3); } catch {}
      try { window.GrivetQuizAnalytics && window.GrivetQuizAnalytics.viewedResults(state.top3); } catch {}
    }
    root.scrollIntoView({ behavior: 'smooth', block: 'start' });
    focusFirstInteractive(current);
  }

  function restoreAnswers(current) {
    const a = state.answers;
    const scope = root.querySelector('[data-screen="' + current + '"]');
    if (!scope) return;
    scope.querySelectorAll('input[type="radio"]').forEach(inp => {
      const n = inp.name;
      let stored = null;
      if (n === 'q1') stored = a.q1;
      else if (n.startsWith('q2_')) stored = a.q2;
      else if (n === 'q3') stored = a.q3;
      else if (n === 'q6') stored = a.q6;
      else if (n === 'q8_gender') stored = a.q8.gender;
      else if (n === 'q8_width') stored = a.q8.width;
      inp.checked = (stored != null && stored !== '' && inp.value === stored);
    });
    scope.querySelectorAll('input[type="checkbox"]').forEach(inp => {
      if (inp.name === 'q5') inp.checked = a.q5.includes(inp.value);
      if (inp.name === 'q4_newbie') inp.checked = a.q4.newbie;
    });
    const loved = scope.querySelector('[name="q4_loved"]'); if (loved) loved.value = a.q4.loved || '';
    const dis = scope.querySelector('[name="q4_disliked"]'); if (dis) dis.value = a.q4.disliked || '';
    const size = scope.querySelector('[name="q8_size"]'); if (size) size.value = a.q8.size || '';
    const cushion = scope.querySelector('[name="q7_cushion"]'); if (cushion) cushion.value = a.q7.cushion;
    const feel = scope.querySelector('[name="q7_feel"]'); if (feel) feel.value = a.q7.feel;
  }

  function updateNavButtons(current) {
    const nav = root.querySelector('[data-screen="' + current + '"] [data-nav]');
    if (!nav) return;
    const prevBtn = nav.querySelector('[data-prev]');
    const nextBtn = nav.querySelector('[data-next]');
    const submitBtn = nav.querySelector('[data-submit]');
    if (prevBtn) prevBtn.hidden = state.stack.length < 2 || current === 'q1';
    if (nextBtn) {
      nextBtn.hidden = current === 'q8';
      nextBtn.disabled = !isStepAnswered(current);
    }
    if (submitBtn) { submitBtn.hidden = current !== 'q8'; submitBtn.disabled = !isStepAnswered(current); }
  }

  function isStepAnswered(current) {
    const a = state.answers;
    switch (current) {
      case 'q1': return !!a.q1;
      case 'q2a': case 'q2b': case 'q2c': case 'q2d': case 'q2e': return !!a.q2;
      case 'q3': return !!a.q3;
      case 'q4': case 'q5': case 'q7': return true;
      case 'q6': return !!a.q6;
      case 'q8': return !!(a.q8.gender && a.q8.size && a.q8.width);
      default: return true;
    }
  }

  /* -------------------------- Navigation --------------------------- */
  function goNext() {
    const current = state.stack[state.stack.length - 1];
    if (!isStepAnswered(current)) return;
    try {
      const ans = state.answers;
      let answerValue = null;
      if (current === 'q1') answerValue = ans.q1;
      else if (current.startsWith('q2')) answerValue = ans.q2;
      else if (current === 'q3') answerValue = ans.q3;
      else if (current === 'q4') answerValue = JSON.stringify(ans.q4);
      else if (current === 'q5') answerValue = ans.q5.join(',');
      else if (current === 'q6') answerValue = ans.q6;
      else if (current === 'q7') answerValue = JSON.stringify(ans.q7);
      const timeOn = Date.now() - lastQuestionStart;
      window.GrivetQuizKlaviyo && window.GrivetQuizKlaviyo.answered(current, answerValue, timeOn);
      window.GrivetQuizAnalytics && window.GrivetQuizAnalytics.answered(current, answerValue, timeOn);
    } catch {}
    lastQuestionStart = Date.now();
    const next = nextScreen(current);
    if (!next) return;
    state.stack.push(next);
    saveState();
    renderCurrent();
    scheduleAbandonCheck();
  }
  function goBack() {
    if (state.stack.length < 2) return;
    state.stack.pop();
    saveState();
    renderCurrent();
  }
  function startQuiz() {
    state.started = true;
    state.stack = ['landing', 'q1'];
    detectTier(); // fire-and-forget
    saveState();
    renderCurrent();
    try { window.GrivetQuizKlaviyo && window.GrivetQuizKlaviyo.started(state); } catch {}
    try { window.GrivetQuizAnalytics && window.GrivetQuizAnalytics.started(state); } catch {}
  }

  /* -------------------------- Abandonment detection (10 min inactivity) --------------------------- */
  let abandonTimer = null;
  let lastQuestionStart = Date.now();
  function scheduleAbandonCheck() {
    clearTimeout(abandonTimer);
    abandonTimer = setTimeout(() => {
      const current = state.stack[state.stack.length - 1];
      if (/^q[1-8]|q2[a-e]$/.test(current)) {
        try { window.GrivetQuizKlaviyo && window.GrivetQuizKlaviyo.abandoned(state); } catch {}
      }
    }, 10 * 60 * 1000);
  }

  /* -------------------------- Labor illusion --------------------------- */
  function runLaborIllusion() {
    const container = root.querySelector('[data-screen="labor"]');
    if (!container) return;
    const shoes = (catalog && catalog.shoes) ? catalog.shoes : [];
    const variantCount = shoes.reduce((sum, s) => sum + (s.variants ? s.variants.length : 0), 0);
    const skuCount = variantCount >= 1000 ? (Math.floor(variantCount / 1000) + 'K+ SKUs') : (variantCount || '18,000+') + ' SKUs';
    const lines = [
      'Matching foot shape and pain signals...',
      'Searching ' + skuCount + ' across every major running and outdoor brand...',
      'Cross-referencing weekly mileage and feel preferences...',
      'Checking live inventory for your exact size...',
      'Prioritizing shoes that match your profile...',
      'Building your Grivet Fit Profile...'
    ];
    container.innerHTML = '<div class="gq-labor"><ul class="gq-labor-list">' +
      lines.map((t, i) => '<li style="--gq-delay:' + (i * 0.55) + 's">' + t + '</li>').join('') +
      '</ul><div class="gq-labor-reveal" data-labor-reveal hidden></div></div>';
    setTimeout(() => {
      const reveal = container.querySelector('[data-labor-reveal]');
      if (reveal) {
        const name = window.GrivetQuizScoring.generateProfileName(state.answers);
        reveal.innerHTML = "You're a <strong>" + name + "</strong>";
        reveal.hidden = false;
      }
    }, 3500);
    // Auto-compute top 3 in background so preview renders instantly
    ensureCatalog().then(() => {
      computeTop3();
      setTimeout(() => {
        if (state.stack[state.stack.length - 1] === 'labor') {
          state.stack.push('preview'); saveState(); renderCurrent();
        }
      }, 4200);
    });
  }

  /* -------------------------- Scoring --------------------------- */
  function ensureCatalog() {
    if (catalog) return Promise.resolve(catalog);
    const url = root.dataset.catalogUrl;
    return fetch(url).then(r => r.json()).then(j => { catalog = j; return j; }).catch(() => { catalog = { shoes: [] }; return catalog; });
  }
  function computeTop3() {
    if (!catalog || !catalog.shoes) return;
    const rec = window.GrivetQuizScoring.selectRecommendations(catalog.shoes, state.answers);
    state.top3 = rec.picks;
    state.recommendationTier = rec.tier;
    // Debug: surface the tier + count in devtools if fewer than 3 picks so we can diagnose sparse results
    if (!state.top3 || state.top3.length < 3) {
      try { console.warn('[grivet-quiz] low pick count', { tier: rec.tier, picks: state.top3.length, catalogSize: catalog.shoes.length, answers: state.answers }); } catch {}
    }
    state.completedAt = new Date().toISOString();
    saveState();
    saveProfile();
    try {
      const name = window.GrivetQuizScoring.generateProfileName(state.answers);
      window.GrivetQuizKlaviyo && window.GrivetQuizKlaviyo.completed(state, state.top3, name);
      window.GrivetQuizAnalytics && window.GrivetQuizAnalytics.completed(state, state.top3, name);
    } catch {}
  }

  /* -------------------------- Geographic tier --------------------------- */
  function detectTier() {
    if (state.tier) return Promise.resolve(state.tier);
    return fetch('https://ipapi.co/json/').then(r => r.json()).then(d => {
      state.detected = { city: d.city || null, region: d.region_code || null, country: d.country_code || null };
      if (d.country_code !== 'US') { state.tier = 'national'; saveState(); return 'national'; }
      const st = (d.region_code || '').toUpperCase();
      const city = (d.city || '').toLowerCase();
      let tier = 'national';
      if (LOCAL_STATES.includes(st)) {
        const metros = LOCAL_METRO_STATES[st] || [];
        if (metros.some(m => city.includes(m))) tier = 'local';
        else if (REGIONAL_STATES.includes(st)) tier = 'regional';
      } else if (REGIONAL_STATES.includes(st)) tier = 'regional';
      state.tier = tier;
      saveState();
      return tier;
    }).catch(() => { state.tier = 'national'; saveState(); return 'national'; });
  }

  /* -------------------------- Preview --------------------------- */
  function renderPreview() {
    const container = root.querySelector('[data-screen="preview"]');
    if (!container || !state.top3) return;
    const name = window.GrivetQuizScoring.generateProfileName(state.answers);
    const reasoning = window.GrivetQuizScoring.buildReasoning(state.answers);
    const labels = ['Top Match', 'Runner-Up', 'Worth Considering'];
    container.innerHTML =
      '<div class="gq-preview-head">' +
        '<div class="gq-preview-kicker">Your Grivet Fit Profile</div>' +
        '<h2 class="gq-preview-name">' + name + '</h2>' +
        '<p class="gq-preview-reason">' + reasoning + '</p>' +
      '</div>' +
      '<div class="gq-preview-cards">' +
        state.top3.map((s, i) =>
          '<div class="gq-preview-card">' +
            '<div class="gq-preview-badge gq-badge-' + i + '">' + labels[i] + '</div>' +
            '<div class="gq-preview-silhouette">' +
              (s.shoe.image ? '<img src="' + s.shoe.image + '" alt="" class="gq-preview-img">' : '') +
              '<div class="gq-preview-blur"></div>' +
            '</div>' +
          '</div>'
        ).join('') +
      '</div>' +
      '<div class="gq-email-gate">' +
        '<h3 class="gq-email-gate-title">See your 3 matches — plus 10% off your first pair.</h3>' +
        '<p class="gq-email-gate-sub">We\'ll save your Grivet Fit Profile so you can skip the quiz next time.</p>' +
        '<form class="gq-email-form" data-email-form novalidate>' +
          '<input type="email" name="email" placeholder="your@email.com" required autocomplete="email" class="gq-input">' +
          '<label class="gq-checkbox-row"><input type="checkbox" name="consent" checked> <span>Send me replacement reminders, training tips, and new gear launches.</span></label>' +
          '<label class="gq-checkbox-row"><input type="checkbox" name="sms_consent"> <span>Text me my matches and occasional deals (≤4/mo)</span></label>' +
          '<input type="tel" name="phone" placeholder="Phone (required for SMS)" autocomplete="tel" class="gq-input" style="display:none" data-phone-field>' +
          '<button type="submit" class="gq-btn gq-btn--primary gq-btn--large">Unlock my matches →</button>' +
          '<div class="gq-email-meta">No spam. Unsubscribe anytime.</div>' +
          '<button type="button" class="gq-email-skip" data-email-skip>Skip — just show me the shoes</button>' +
        '</form>' +
      '</div>';
  }

  function handleEmailSubmit(form) {
    const email = form.elements.email.value.trim();
    if (!email || !/^[^@]+@[^@]+\.[^@]+$/.test(email)) { form.elements.email.focus(); return; }
    const smsOptIn = form.elements.sms_consent.checked;
    const phone = form.elements.phone.value.trim();
    if (smsOptIn && !phone) { form.querySelector('[data-phone-field]').focus(); return; }
    state.emailCaptured = email;
    state.smsOptIn = smsOptIn;
    saveState();
    saveProfile();
    try {
      const name = window.GrivetQuizScoring.generateProfileName(state.answers);
      window.GrivetQuizKlaviyo && window.GrivetQuizKlaviyo.emailCaptured(state, email, smsOptIn, phone, state.top3, name);
      window.GrivetQuizAnalytics && window.GrivetQuizAnalytics.emailCaptured(email, smsOptIn);
    } catch {}
    state.stack.push('results'); saveState(); renderCurrent();
  }

  /* -------------------------- Results --------------------------- */
  function renderResults() {
    const container = root.querySelector('[data-screen="results"]');
    if (!container || !state.top3) return;

    // Empty-state safety net — never render blank result cards
    if (!state.top3.length) {
      const name = window.GrivetQuizScoring.generateProfileName(state.answers);
      const activityPath = {
        run_road: '/collections/running', run_trail: '/collections/trail-running',
        hike: '/collections/hiking', lifestyle: '/collections/lifestyle', gift: '/collections/running'
      }[state.answers.q1] || '/collections/running';
      container.innerHTML =
        '<div class="gq-results-head">' +
          '<div class="gq-preview-kicker">Your Grivet Fit Profile</div>' +
          '<h2 class="gq-preview-name">' + (name || 'Athlete') + '</h2>' +
        '</div>' +
        '<div class="gq-results-fallback" role="status">' +
          'We couldn\'t find a shoe in our current lineup that matches every one of your answers. ' +
          'Our team can help — text us your profile or browse our full selection below.' +
        '</div>' +
        '<div class="gq-results-footer" style="justify-content:center;flex-wrap:wrap;gap:12px;">' +
          '<a class="gq-btn gq-btn--primary" href="' + activityPath + '">Browse our full collection</a>' +
          '<a class="gq-btn gq-btn--ghost" href="/pages/contact-us">Ask our fit team</a>' +
          '<button type="button" class="gq-btn gq-btn--ghost" data-retake>Retake the quiz</button>' +
        '</div>';
      return;
    }
    const name = window.GrivetQuizScoring.generateProfileName(state.answers);
    const reasoning = window.GrivetQuizScoring.buildReasoning(state.answers);
    const labels = ['Top Match', 'Runner-Up', 'Worth Considering'];
    const tier = state.tier || 'national';

    const cards = state.top3.map((s, i) => {
      const shoe = s.shoe;
      const variant = findBestVariant(shoe, state.answers);
      const pickup = (tier === 'local') ? '<label class="gq-card-pickup"><input type="checkbox" data-pickup><span>Pick up free at your local Grivet store</span></label>' : '';
      return '<article class="gq-result-card">' +
        '<div class="gq-result-badge gq-badge-' + i + '">' + labels[i] + '</div>' +
        '<a class="gq-result-media" href="' + shoe.url + '">' +
          (shoe.image ? '<img src="' + shoe.image + '" alt="' + shoe.title + '">' : '') +
        '</a>' +
        '<div class="gq-result-brand">' + shoe.brand + '</div>' +
        '<a class="gq-result-title" href="' + shoe.url + '">' + shoe.title + '</a>' +
        '<div class="gq-result-price">$' + shoe.price.toFixed(2) + '</div>' +
        '<div class="gq-result-why">Matched because ' + (s.reasons.slice(0, 2).join(' + ') || 'it fits your profile') + '.</div>' +
        (variant ? '<div class="gq-result-stock">✓ In stock — ships today</div>' : '<div class="gq-result-stock gq-result-stock--out">Size unavailable</div>') +
        pickup +
        '<button type="button" class="gq-btn gq-btn--primary gq-result-atc" data-atc data-variant="' + (variant ? variant.id : '') + '" data-sku="' + (variant ? variant.sku : '') + '" data-position="' + (i+1) + '" ' + (variant ? '' : 'disabled') + '>Add to cart</button>' +
      '</article>';
    }).join('');

    // Bundle/upsell section removed until it's fully built out.
    const bundleHtml = '';

    const inStore = renderInStoreCTA(tier);

    const fallbackNote = (state.top3[0] && state.top3[0].fallback_note) || '';
    const fallbackBanner = fallbackNote
      ? '<div class="gq-results-fallback" role="status">' + fallbackNote + '</div>'
      : '';

    container.innerHTML =
      '<div class="gq-results-head">' +
        '<div class="gq-preview-kicker">Your Grivet Fit Profile</div>' +
        '<h2 class="gq-preview-name">' + name + '</h2>' +
        '<p class="gq-preview-reason">' + reasoning + '</p>' +
        (state.emailCaptured ? '<p class="gq-results-code">Your 10% code was just emailed to <strong>' + state.emailCaptured + '</strong>.</p>' : '') +
      '</div>' +
      fallbackBanner +
      '<div class="gq-result-cards">' + cards + '</div>' +
      bundleHtml +
      inStore +
      '<div class="gq-results-footer">' +
        '<button type="button" class="gq-btn gq-btn--ghost" data-retake>Retake the quiz</button>' +
        '<a class="gq-btn gq-btn--ghost" href="/collections/running">Browse all running shoes →</a>' +
      '</div>';
  }

  function findBestVariant(shoe, answers) {
    if (!shoe.variants || !shoe.variants.length) return null;
    const size = String(answers.q8.size || '').replace(/^0+/, '');
    const wantedWidth = answers.q8.width;
    return shoe.variants.find(v => {
      if (!v.available) return false;
      const sizeMatch = v.size ? String(v.size).replace(/^0+/, '') === size : true;
      const widthMatch = wantedWidth === 'unsure' || !wantedWidth || !v.width || v.width === wantedWidth;
      return sizeMatch && widthMatch;
    }) || shoe.variants.find(v => v.available) || null;
  }

  function buildBundle(answers) {
    const items = [];
    const q1 = answers.q1;
    if (q1 === 'run_road' || q1 === 'run_trail') {
      items.push({ label: 'Balega Hidden Comfort Socks', desc: 'Cushy no-show socks built for running' });
    } else if (q1 === 'hike') {
      items.push({ label: 'Darn Tough Micro Crew', desc: 'Merino socks, lifetime guarantee' });
    }
    if ((answers.q3 === 'arch_low' || answers.q3 === 'arch_high') && q1 !== 'lifestyle') {
      items.push({ label: 'Superfeet Insole', desc: 'Targeted arch support to match your foot' });
    }
    if ((answers.q5 || []).includes('pain_blisters')) {
      items.push({ label: 'Body Glide Balm', desc: 'Anti-chafe stick — no more hot spots' });
    }
    return items.slice(0, 3);
  }

  function renderInStoreCTA(tier) {
    if (tier === 'local') {
      return '<section class="gq-instore gq-instore--local">' +
        '<h3 class="gq-instore-title">Live near one of our Memphis stores?</h3>' +
        '<p>Book a free 30-minute fitting with a Grivet expert.</p>' +
        '<a class="gq-btn gq-btn--primary" href="/pages/store-locations">Book my fitting →</a>' +
      '</section>';
    }
    if (tier === 'regional') {
      return '<section class="gq-instore gq-instore--regional">' +
        '<p>Road-tripping through Memphis soon? We offer free expert fittings at our 4 stores across the metro. <a href="/pages/store-locations">See store details →</a></p>' +
      '</section>';
    }
    return '<section class="gq-instore gq-instore--national">' +
      '<h3 class="gq-instore-title">Free shipping on orders over $99.</h3>' +
      '<p>Free returns within 60 days. Not sure about fit? Our team answers questions by email and phone — <a href="/pages/contact-us">contact us</a>.</p>' +
    '</section>';
  }

  /* -------------------------- Cart --------------------------- */
  function addToCart(variantId, pickup) {
    const body = { id: variantId, quantity: 1 };
    if (pickup) body.properties = { _fulfillment: 'pickup' };
    return fetch((window.Shopify && Shopify.routes ? Shopify.routes.root : '/') + 'cart/add.js', {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body)
    }).then(r => r.json());
  }

  /* -------------------------- Input handlers --------------------------- */
  root.addEventListener('change', (e) => {
    const t = e.target; if (!t || !t.name) return;
    const a = state.answers; const n = t.name;
    if (n === 'q1') { a.q1 = t.value; a.q2 = null; }
    else if (n.startsWith('q2_')) { a.q2 = t.value; }
    else if (n === 'q3') { a.q3 = t.value; }
    else if (n === 'q4_loved') { a.q4.loved = t.value; }
    else if (n === 'q4_disliked') { a.q4.disliked = t.value; }
    else if (n === 'q4_newbie') {
      a.q4.newbie = t.checked;
      if (t.checked) { a.q4.loved = ''; a.q4.disliked = ''; }
      const scope = root.querySelector('[data-screen="q4"]');
      scope.querySelectorAll('[name="q4_loved"],[name="q4_disliked"]').forEach(i => i.disabled = t.checked);
    }
    else if (n === 'q5') {
      const v = t.value;
      if (v === 'none') {
        a.q5 = t.checked ? ['none'] : [];
        const scope = root.querySelector('[data-screen="q5"]');
        scope.querySelectorAll('input[name="q5"]').forEach(i => { if (i.value !== 'none') i.checked = false; });
      } else {
        a.q5 = a.q5.filter(x => x !== 'none');
        a.q5 = t.checked ? [...a.q5, v] : a.q5.filter(x => x !== v);
      }
    }
    else if (n === 'q6') { a.q6 = t.value; }
    else if (n === 'q7_cushion') { a.q7.cushion = Number(t.value); }
    else if (n === 'q7_feel') { a.q7.feel = Number(t.value); }
    else if (n === 'q8_gender') { a.q8.gender = t.value; }
    else if (n === 'q8_size') { a.q8.size = t.value; }
    else if (n === 'q8_width') { a.q8.width = t.value; }
    else if (n === 'sms_consent') {
      const phoneField = root.querySelector('[data-phone-field]');
      if (phoneField) phoneField.style.display = t.checked ? 'block' : 'none';
    }
    saveState();
    updateNavButtons(state.stack[state.stack.length - 1]);
  });

  root.addEventListener('click', (e) => {
    const t = e.target.closest('button, a');
    if (!t) return;
    if (t.hasAttribute('data-start')) { e.preventDefault(); startQuiz(); return; }
    if (t.hasAttribute('data-next')) { e.preventDefault(); goNext(); return; }
    if (t.hasAttribute('data-prev')) { e.preventDefault(); goBack(); return; }
    if (t.hasAttribute('data-submit')) {
      e.preventDefault();
      const current = state.stack[state.stack.length - 1];
      if (!isStepAnswered(current)) return;
      state.stack.push('labor'); saveState(); renderCurrent();
      return;
    }
    if (t.hasAttribute('data-email-skip')) {
      e.preventDefault();
      try {
        const name = window.GrivetQuizScoring.generateProfileName(state.answers);
        window.GrivetQuizKlaviyo && window.GrivetQuizKlaviyo.emailSkipped(name);
      } catch {}
      state.stack.push('results'); saveState(); renderCurrent();
      return;
    }
    if (t.hasAttribute('data-slider-unsure')) {
      e.preventDefault();
      state.answers.q7.cushion = 3; state.answers.q7.feel = 3; state.answers.q7.unsure = true;
      const scope = root.querySelector('[data-screen="q7"]');
      scope.querySelector('[name="q7_cushion"]').value = 3;
      scope.querySelector('[name="q7_feel"]').value = 3;
      saveState();
      return;
    }
    if (t.hasAttribute('data-retake')) {
      e.preventDefault();
      const prevProfile = loadProfile();
      const prevName = prevProfile ? window.GrivetQuizScoring.generateProfileName(prevProfile.answers) : null;
      state = JSON.parse(JSON.stringify(initialState));
      saveState();
      renderCurrent();
      try {
        if (prevName) {
          const daysSince = prevProfile ? Math.round((Date.now() - prevProfile.savedAt) / 86400000) : 0;
          window.GrivetQuizKlaviyo && window.GrivetQuizKlaviyo.retook(prevName, null, daysSince);
        }
      } catch {}
      return;
    }
    if (t.hasAttribute('data-atc')) {
      e.preventDefault();
      const variantId = t.dataset.variant;
      if (!variantId) return;
      const card = t.closest('.gq-result-card');
      const pickup = card && card.querySelector('[data-pickup]') && card.querySelector('[data-pickup]').checked;
      const position = Number(t.dataset.position) || 1;
      const types = ['top', 'value', 'alt'];
      const typeLabel = types[position - 1] || 'top';
      const item = state.top3 && state.top3[position - 1];
      if (item) {
        try {
          window.GrivetQuizKlaviyo && window.GrivetQuizKlaviyo.clickedRecommendation(item.shoe, position, typeLabel);
          window.GrivetQuizAnalytics && window.GrivetQuizAnalytics.clickedRecommendation(item.shoe, position);
        } catch {}
      }
      t.disabled = true; t.textContent = 'Adding…';
      addToCart(variantId, pickup).then(() => {
        if (item) {
          const v = (item.shoe.variants || []).find(x => String(x.id) === String(variantId));
          try {
            window.GrivetQuizKlaviyo && window.GrivetQuizKlaviyo.addedShoeToCart(item.shoe, v, pickup);
            window.GrivetQuizAnalytics && window.GrivetQuizAnalytics.addedShoeToCart(item.shoe, v, pickup);
          } catch {}
        }
        t.textContent = pickup ? 'Reserved for pickup ✓' : 'Added to cart ✓';
        setTimeout(() => { window.location.href = (window.Shopify && Shopify.routes ? Shopify.routes.cart : '/cart'); }, 700);
      }).catch(() => { t.textContent = 'Try again'; t.disabled = false; });
      return;
    }
  });

  root.addEventListener('submit', (e) => {
    const f = e.target.closest('[data-email-form]');
    if (!f) return;
    e.preventDefault();
    handleEmailSubmit(f);
  });

  /* -------------------------- Deep link + boot --------------------------- */
  function applyDeepLink() {
    const params = new URLSearchParams(window.location.search);
    const activity = params.get('activity');
    if (activity && DEEP_LINK_MAP[activity]) {
      state.answers.q1 = DEEP_LINK_MAP[activity];
      state.stack = ['landing', 'q1', nextScreen('q1')];
      saveState();
    }
  }

  // Preload catalog in background so scoring is instant when Q8 submits
  ensureCatalog();
  // Fire-and-forget tier detection even before quiz starts
  detectTier();
  applyDeepLink();
  renderCurrent();
})();
